import { assert } from '@ember/debug';
import { fn } from '@ember/helper';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { isNone, isBlank } from '@ember/utils';
import type Model from '@ember-data/model';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { join } from '@nullvoxpopuli/ember-composable-helpers';
import truncate from 'ember-cli-string-helpers/helpers/truncate';
import { task, dropTask, restartableTask, timeout } from 'ember-concurrency';
import PowerSelect from 'ember-power-select/components/power-select';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, or } from 'ember-truth-helpers';
import BlocklistFilterModal from 'garaje/components/blocklist-filter-modal';
import BlocklistFiltersSearchInput from 'garaje/components/security/configure-blocklist/search-input';
import SlideToggle from 'garaje/components/slide-toggle';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import ButtonGroup from 'garaje/components/ui/button-group';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import DropdownMenu from 'garaje/components/ui/classic/dropdown-menu';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import VirtualEach from 'garaje/components/virtual-each';
import dateFormat from 'garaje/helpers/date-format';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type User from 'garaje/models/user';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type ExporterService from 'garaje/services/exporter';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type ReactRendererService from 'garaje/services/react-renderer';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import type UploaderFactoryService from 'garaje/services/uploader-factory';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import type { RecordArray } from 'garaje/utils/type-utils';
import { Permission, PermissionType } from 'garaje/utils/ui-permissions';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';
import LiquidUnless from 'liquid-fire/components/liquid-unless';
import type { Root } from 'react-dom/client';

// SafeString via a stable public API (htmlSafe's return type) rather than the private
// `@ember/template/-private/handlebars` path, which a Glimmer/Ember minor could reorganize.
type SafeString = ReturnType<typeof htmlSafe>;

const MINIMUM_BLOCKLIST_RECORD_THRESHOLD = 2;

interface SecurityConfigureBlocklistSignature {
  Args: {
    /**
     * array (or array-like Ember Data result) of all blocklist filters to display
     */
    blocklistFilters: BlacklistFilter[];
    /**
     * string representing the route to the blocklist CSV upload page
     */
    csvImportRoute: string;
    /**
     * string with the name of the relationship to which a new filter entry should be saved (e.g., "location" or "property")
     */
    filterParentRelationshipName: string;
    /**
     * Ember Data model of the parent to which a new filter entry should be attached
     */
    filterParent: Model;
    /**
     * function triggered when list of contacts is changed or saved. Received a single Boolean argument, indicating whether the list is changed & unsaved (true) or not (false).
     */
    onContactsChange?: (hasChanges: boolean) => void;
    /**
     * function to handle saving new list of blocklist notification contacts. Receives one argument (an array of all currently-selected contacts). If this function returns a promise it is awaited.
     */
    saveBlocklistContacts: (users: User[]) => Promise<Model>;
    /**
     * function to load blocklist filters. should return an array or array-like Ember Data result (or a Promise that resolves to that)
     */
    loadBlocklistFilters: (locationView?: 'this' | 'all') => Promise<BlacklistFilter[]>;
    /**
     * function to load blocklist contacts. should return an array or array-like Ember Data result (or a Promise that resolves to that)
     */
    loadBlocklistContacts: () => Promise<User[]>;
    /**
     * function to return a filtered list of admins that can be selected as notification contacts. Receives one argument (a string representing the current search query) and should return an array of User models, an array-like Ember Data result, or a Promise that resolves to either of those
     */
    searchUsers: (term: string) => Promise<RecordArray<User>>;
    /**
     * boolean indicating whether the blocklist should be read-only or not (blocklist contacts and filter records)
     */
    readOnly?: boolean;
    /**
     * boolean indicating whether the user has the ability to upload a blocklist CSV
     */
    hasUploadPermission?: boolean;
    /**
     * boolean indicating whether the user has the ability to create blocklist filter records
     */
    hasCreatePermission?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: [];
  };
}

/**
 * Base component for common blocklist configuration features. You almost certainly do not want to use
 * this component directly; instead, use one of the more specific components (or create your own as needed):
 * - <Security::ConfigureBlocklist::Location> for managing location/company blocklist
 * - <Security::ConfigureBlocklist::Property> for managing a multi-tenant property blocklist
 */
export default class ConfigureBlocklist extends Component<SecurityConfigureBlocklistSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare uploaderFactory: UploaderFactoryService;
  @service declare exporter: ExporterService;
  @service declare reactRenderer: ReactRendererService;

  @tracked searchText = '';
  @tracked blocklistFilterInModal: BlacklistFilter | null = null;
  // Whether the open edit/view modal is read-only. A location admin who can't edit a given filter
  // (isRowReadOnly) opens it as a view-only modal; set whenever the modal is opened (edit or create).
  @tracked blocklistFilterModalReadOnly = false;
  // The filter whose "where this block applies" scope is being edited in the React modal.
  @tracked blocklistFilterToScope: BlacklistFilter | null = null;
  // Live React root for the mounted SelectActiveLocationsModal; unmounted on close/destroy.
  private scopeModalRoot?: Root;
  @tracked blocklistFilterToDelete: BlacklistFilter | null = null;
  @tracked blocklistFilterToToggle: BlacklistFilter | null = null;
  @tracked blocklistContacts: User[] = [];
  @tracked blocklistFilters: BlacklistFilter[] = [];
  // True when the most recent filter load threw. The load task swallows its rejection (to flash an
  // error), so this flag is what makes the table's error empty-state reachable instead of "No one
  // blocked yet" masking a failed load of a security list.
  @tracked blocklistLoadFailed = false;
  @tracked hasChangedBlocklistContacts = false;
  @tracked tempPhoto: string | ArrayBuffer | null = null;
  @tracked tempId: string | null = null;
  // The table's This/All-locations view filter (default All). Threads to the loader, which maps
  // 'this' → filter[applied-location]=<currentLocationId> and 'all' → filter[company]=<id>.
  @tracked locationView: 'this' | 'all' = 'all';

  /**
   * Feature flag for blocklist enhancements: expiration dates, toggle, delete, new table layout
   */
  get hasBlocklistEnhancements(): boolean {
    return this.featureFlags.isEnabled('feat-blocklist-expiration-and-pause');
  }

  /**
   * Feature flag for blocklist location scoping (Locations column, This/All filter, React scope modal).
   * Do NOT ramp before the BE write-contract + edit-gate deploy, or a location admin's out-of-scope
   * additions would succeed silently — a cross-location privilege escalation.
   */
  get hasLocationScoping(): boolean {
    return this.featureFlags.isEnabled('feat-blocklist-location-scoping');
  }

  /**
   * The location-scoping UI (Locations column, This/All filter, per-row gate) applies ONLY to the
   * location/company blocklist variant — never the property/zone variant (zones are out of scope and
   * carry a nil location_scope). Gates on the flag AND the variant.
   */
  get showLocationScoping(): boolean {
    return this.hasLocationScoping && this.args.filterParentRelationshipName === 'location';
  }

  /**
   * Whether the current admin may scope a filter to ALL locations (company-wide), gated by the
   * company-scoped `visitors.global_blocklist.update` permission (not the legacy is-global-admin role).
   * Fails closed: until the BE ships that permission, `hasPermissionAtCurrentCompany` returns false and
   * the "All locations" radio stays disabled. Defense-in-depth — the BE policy (403) is authoritative.
   */
  get canEditAllLocations(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.VISITORS_GLOBAL_BLOCKLIST_UPDATE);
  }

  /**
   * The location ids the current admin may LINK a filter to, computed client-side so the React picker
   * can gray out the rest. A location is linkable when the admin holds `visitors.blocklist.update`
   * there; a company-wide admin gets the full set (null = no client-side restriction). Defense-in-depth —
   * the BE policy (403) still backstops any out-of-scope save.
   */
  get allowedLocationIds(): string[] | null {
    if (this.canEditAllLocations) return null;
    return this.skinnyLocations.currentCompanyLocations
      .filter((loc) => this.authz.hasPermission(PermissionType.LOCATION, loc.id, Permission.VISITORS_BLOCKLIST_UPDATE))
      .map((loc) => loc.id);
  }

  /**
   * When toggling, if disabled=true (currently unblocked), we're about to BLOCK them.
   * If disabled=false (currently blocked), we're about to UNBLOCK them.
   */
  get isBlockingAction(): boolean {
    return this.blocklistFilterToToggle?.disabled === true;
  }

  get timezone(): string {
    return this.state.currentLocation?.timezone ?? 'America/Los_Angeles';
  }

  /**
   * Single source of truth for the new-table (feat-blocklist-expiration-and-pause) column tracks,
   * applied to BOTH the header row and every body row via `style` so the two can never drift. The
   * Locations track exists only when the scoping column renders; Reason grows, every other column is
   * fixed. Paired with `scrollbar-gutter:stable` on the header and the body scroll container so the
   * flexible Reason column stays width-aligned whether or not a vertical scrollbar is present.
   */
  get newTableGridStyle(): SafeString {
    // Flexible fr/minmax tracks so the grid fits the settings content area with NO horizontal scroll
    // (cells truncate via min-w-0 + TruncateWithTooltip). Blocked stays a fixed track — it holds
    // fixed-size controls (toggle + edit + delete), not text.
    const tracks = [
      'minmax(180px,1.6fr)',
      'minmax(100px,0.9fr)',
      'minmax(120px,1.1fr)',
      'minmax(120px,1.6fr)',
      'minmax(120px,0.9fr)',
    ];
    // Locations needs room for the longest label ("N other locations") with breathing space before
    // the Blocked column — a 110px min clamped it tight against the gap.
    if (this.showLocationScoping) tracks.push('minmax(140px,1.1fr)');
    tracks.push('132px');
    return htmlSafe(`display:grid; grid-template-columns:${tracks.join(' ')}; column-gap:1rem;`);
  }

  /**
   * Tailwind class helpers for the new table layout (feature flag: feat-blocklist-expiration-and-pause).
   * Column widths come from the shared grid template (newTableGridStyle); these helpers carry only the
   * per-cell concerns (inner layout, truncation, disabled de-emphasis). min-w-0 lets each grid cell
   * shrink below its content so truncation works (grid items default to min-width:auto).
   */
  getRowClass(disabled: boolean): string {
    const base = 'grid items-center px-6 min-h-[58px] w-full cursor-pointer border-b border-[#e9e9ea]';
    // bg-[#fcfcfc] is a subtle gray for disabled rows (between white and carbon-5)
    return disabled ? `${base} bg-[#fcfcfc]` : base;
  }

  // Per-column cell class: the base layout comes from the template, disabled rows are de-emphasized.
  getColumnClass(base: string, disabled: boolean): string {
    return disabled ? `${base} opacity-40` : base;
  }

  /**
   * The Locations-column label for a filter, derived from the filter's relationships AND the
   * currently-viewed location (`state.currentLocation.id`). Three states:
   * - "All locations"          — company-wide (scope = all_locations).
   * - "{N} location(s)"        — specific AND the current location IS in the set (blocked here + maybe others).
   * - "{N} other locations"    — specific AND the current location is NOT in the set (blocked elsewhere only).
   * Edge: no current-location context (global/all view) → specific rows render as "{N} locations".
   * Returns '' for zone/property filters (scope = zone/null) so the cell is blank, not "0 …".
   * Arrow function: it reads `this.state`/`this.currentLocationId`, so it must bind `this` lexically —
   * a plain method invoked as a template helper is called via FunctionHelperManager WITHOUT `this`.
   */
  locationScopeLabel = (filter: BlacklistFilter): string => {
    if (filter.scope === 'all_locations') return 'All locations';
    if (filter.scope === 'specific_locations') {
      const count = filter.locationsCount;
      const currentId = this.currentLocationId;
      // No current-location context (global/all view): no "other" form is possible.
      if (currentId === null) {
        return count === 1 ? '1 location' : `${count} locations`;
      }
      if (filter.locationIds.includes(currentId)) {
        return count === 1 ? '1 location' : `${count} locations`;
      }
      return count === 1 ? '1 other location' : `${count} other locations`;
    }
    return '';
  };

  /** The id of the location currently being viewed, or null in a global/all-locations context. */
  get currentLocationId(): string | null {
    return this.state.currentLocation?.id ?? null;
  }

  /**
   * Per-row edit gate: trusts the BE's authoritative `permissions.can-edit` (fails closed when absent)
   * rather than re-deriving from roles. Arrow function so it works as a template helper
   * (FunctionHelperManager strips `this`).
   */
  canEditFilter = (filter: BlacklistFilter): boolean => filter.canEdit;

  /**
   * A row is read-only when the component is read-only, or (with location scoping on) the BE says this
   * admin can't edit this particular filter (`permissions.can-edit` false). Both this and
   * `canEditFilter` are ARROW functions: Glimmer invokes a template-called method via
   * FunctionHelperManager WITHOUT `this` binding, so a plain method reading `this.args` would throw.
   */
  isRowReadOnly = (filter: BlacklistFilter): boolean => {
    if (this.args.readOnly) return true;
    if (this.showLocationScoping) return !this.canEditFilter(filter);
    return false;
  };

  get aboveMinimumBlocklistRecordThreshold(): boolean {
    return this.blocklistFilters.length > MINIMUM_BLOCKLIST_RECORD_THRESHOLD;
  }

  get sortedBlocklistFilters(): BlacklistFilter[] {
    assert('You must pass @blocklistFilters to <Security::ConfigureBlocklist>', !isNone(this.args.blocklistFilters));

    if (this.hasBlocklistEnhancements) {
      // New sorting: 1) enabled items first (disabled=false), 2) then alphabetically by fullName
      return this.args.blocklistFilters.toSorted((a, b) => {
        // Disabled items go to the bottom
        if (a.disabled !== b.disabled) {
          return a.disabled ? 1 : -1;
        }
        // Within same disabled status, sort alphabetically
        return (a.fullName || '').localeCompare(b.fullName || '');
      });
    }

    // Original sorting: just alphabetically by fullName
    return this.args.blocklistFilters.toSorted((a, b) => {
      return (a.fullName || '').localeCompare(b.fullName || '');
    });
  }

  /*
   * Filters blocklistFilters based on the current value of 'searchText'
   */
  get filteredBlocklistFilters(): BlacklistFilter[] {
    const { searchText, sortedBlocklistFilters } = this;

    let filtered = sortedBlocklistFilters;

    // Client-side "This location" filter over the already-loaded set: keep company-wide records
    // (they apply everywhere, incl. here) plus records linked to the current location. Reading
    // `locationView` here makes the table recompute on the dropdown flip with no re-fetch/fade.
    if (this.showLocationScoping && this.locationView === 'this') {
      const currentLocationId = this.state.currentLocation?.id;
      filtered = filtered.filter(
        (blocklistFilter) =>
          blocklistFilter.scope === 'all_locations' ||
          (currentLocationId != null && blocklistFilter.locationIds.includes(currentLocationId)),
      );
    }

    if (isBlank(searchText)) {
      return filtered;
    }

    const lowerSearchText = searchText.toLowerCase();
    return filtered.filter((blocklistFilter) => {
      return blocklistFilter.searchable.toLowerCase().indexOf(lowerSearchText) !== -1;
    });
  }

  async loadBlocklistFilters(): Promise<void> {
    // The This/All-locations toggle is a pure CLIENT-SIDE filter over the already-loaded set (see
    // `filteredBlocklistFilters` + `setLocationView`), so always load the FULL company list here.
    // Threading the narrowed view to the loader (filter[applied-location]) would fetch only
    // company-wide + current-location rows; switching to "All locations" — which does NOT re-fetch —
    // could then never reveal other-location filters. The full company set (filter[company]) is also
    // the wire shape the live BE already serves, so this needs no not-yet-shipped BE behavior.
    const blocklistFilters = await this.args.loadBlocklistFilters();
    this.blocklistFilters = blocklistFilters.slice();
  }

  // Switch the table between "This location" and "All locations". Pure client-side filter over the
  // already-loaded set (see filteredBlocklistFilters) — no re-fetch, so no LiquidUnless fade.
  setLocationView = (view: 'this' | 'all'): void => {
    if (this.locationView === view) return;
    this.locationView = view;
  };

  // A STABLE array instance (not a getter) so the option `selectedLocationViewOption` finds is
  // identity-equal (===) to an entry PowerSelect receives via @options — that identity is how
  // ember-power-select marks the open dropdown's selected option (aria-selected + highlight).
  locationViewOptions: Array<{ value: 'this' | 'all'; label: string }> = [
    { value: 'all', label: 'All locations' },
    { value: 'this', label: 'This location' },
  ];

  get selectedLocationViewOption(): { value: 'this' | 'all'; label: string } | undefined {
    return this.locationViewOptions.find((option) => option.value === this.locationView);
  }

  onLocationViewSelect = (option: { value: 'this' | 'all'; label: string }): void => {
    this.setLocationView(option.value);
  };

  @action
  onInsert(): void {
    // Land non-global admins on the This-location view (the records most relevant to them); global
    // admins keep the company-wide All view. The per-row edit gate is independent of the view.
    if (this.showLocationScoping && !this.currentAdmin.isGlobalAdmin) {
      this.locationView = 'this';
    }
    void this.initBlocklistFiltersTask.perform();
    void this.initBlocklistContactsTask.perform();
  }

  @action
  onDestroy(): void {
    if (
      this.blocklistFilterInModal &&
      Boolean(this.blocklistFilterInModal.isNew) &&
      !this.blocklistFilterInModal.isDestroying
    ) {
      this.blocklistFilterInModal.unloadRecord();
    }
  }

  mutateSearchText = restartableTask(async (searchText: string) => {
    await timeout(zft(200));

    this.searchText = searchText;
  });

  exportBlocklistFiltersAsCsv = dropTask(async () => {
    assert('You must pass @blocklistFilters to <Security::ConfigureBlocklist>', !isNone(this.args.blocklistFilters));
    await timeout(zft(200));
    try {
      await this.exporter.exportCsv.perform('blocklist-export.csv', this.args.blocklistFilters);
      this.flashMessages.showAndHideFlash('success', 'Block list downloaded!');
    } catch {
      this.flashMessages.showFlash('error', 'Block list download failed');
    }
  });

  initBlocklistFiltersTask = task(async () => {
    assert(
      'You must pass a @loadBlocklistFilters function as an argument to <Security::ConfigureBlocklist>',
      typeof this.args.loadBlocklistFilters === 'function',
    );
    this.blocklistLoadFailed = false;
    try {
      await this.loadBlocklistFilters();
    } catch (e) {
      this.blocklistLoadFailed = true;
      this.flashMessages.showAndHideFlash('error', 'Error', parseErrorForDisplay(e));
    }
  });

  initBlocklistContactsTask = task(async () => {
    assert(
      'You must pass @loadBlocklistContacts to <Security::ConfigureBlocklist>',
      typeof this.args.loadBlocklistContacts === 'function',
    );
    try {
      this.blocklistContacts = await this.args.loadBlocklistContacts();
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'Error', parseErrorForDisplay(e));
    }
  });

  searchUsersTask = task(async (term: string) => {
    assert('You must pass @searchUsers to <Security::ConfigureBlocklist>', typeof this.args.searchUsers === 'function');
    const currentContactsIds = this.blocklistContacts.map((user) => user.id);
    const users = await this.args.searchUsers(term);

    const lowerSearchText = term.toLowerCase();

    return users
      .uniqBy('id') // dedup since roles could point to same user
      .reject(({ id }) => currentContactsIds.includes(id)) // remove existing contacts
      .filter(({ fullName }) => fullName.toLowerCase().indexOf(lowerSearchText) !== -1);
  });

  saveBlocklistContactsTask = task(async () => {
    assert(
      'You must pass @saveBlocklistContacts to <Security::ConfigureBlocklist>',
      typeof this.args.saveBlocklistContacts === 'function',
    );

    try {
      await this.args.saveBlocklistContacts(this.blocklistContacts);
      this.hasChangedBlocklistContacts = false;
      this.args.onContactsChange?.(false);
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error adding administrator', 'Please try again.');
    }
  });

  uploadPhotoTask = task(async (photo: File, id: string) => {
    try {
      const uploader = this.uploaderFactory.createUploader({
        url: urlBuilder.v3.blocklistFilter.photoUpload(id),
        method: 'POST',
        headers: { Accept: 'application/vnd.api+json' },
      });
      await uploader.upload(photo);
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error uploading photo', 'Please try again.');
    }
  });

  deletePhotoTask = task(async (id: string) => {
    try {
      await this.ajaxFetch.del(urlBuilder.v3.blocklistFilter.photoUpload(id));
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error removing photo', 'Please try again.');
    }
  });

  saveBlocklistFilterTask = task(async (blocklistFilter: BlacklistFilter, photo: File | null) => {
    const isNew = Boolean(blocklistFilter.isNew);
    const editedFields = Object.keys(blocklistFilter.changedAttributes());
    const shouldDeletePhoto =
      !isNew &&
      !photo &&
      !blocklistFilter.photo.dataUrl &&
      !blocklistFilter.photo.original &&
      editedFields.includes('photo');

    try {
      if (photo) this.tempPhoto = blocklistFilter.photo.dataUrl;

      this.metrics.trackJobEvent(`Security - Blocked Person ${isNew ? 'Creation' : 'Update'} Requested`, {
        block_list_filter_id: blocklistFilter.id,
        edited_fields: editedFields,
      });

      if (shouldDeletePhoto) {
        // delete the photo before we save the record so that the photo is gone by the time the save updates the record
        await this.deletePhotoTask.perform(blocklistFilter.id);
      }

      await blocklistFilter.save();
      await this.loadBlocklistFilters();

      this.metrics.trackJobEvent(`Security - Blocked Person Record ${isNew ? 'Created' : 'Updated'}`, {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'success',
      });

      this.tempId = blocklistFilter.id;
      this.blocklistFilterInModal = null;

      const message_title = isNew ? 'Saved!' : 'Updated!';

      this.flashMessages.showAndHideFlash('success', message_title);
      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'success',
        message_title,
        message_code: isNew ? 'blocked_person_create_success' : 'blocked_person_update_success',
        message_code_type: 'block_list',
      });

      if (photo) {
        await this.uploadPhotoTask.perform(photo, blocklistFilter.id);
        await blocklistFilter.reload(); // reload the filter to get the uploaded photo
      }

      this.tempPhoto = null;
      this.tempId = null;
    } catch (e) {
      const error_message = parseErrorForDisplay(e);
      this.metrics.trackJobEvent(`Security - Blocked Person Record ${isNew ? 'Created' : 'Updated'}`, {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'failure',
        error_message,
      });

      const message_title = isNew ? 'Save failed' : 'Update failed';
      this.flashMessages.showAndHideFlash('error', message_title, 'Please try again.');

      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'error',
        message_title,
        message_code: error_message,
        message_code_type: 'block_list',
      });
    }
  });

  deleteBlocklistFilterTask = task(async (blocklistFilter: BlacklistFilter) => {
    this.metrics.startJob();
    try {
      this.metrics.trackJobEvent(`Security - Blocked Person Deletion Requested`, {
        block_list_filter_id: blocklistFilter.id,
      });

      const updatedBlocklistFilters = this.blocklistFilters.filter((item) => item.id !== blocklistFilter.id);
      await blocklistFilter.destroyRecord();
      this.blocklistFilters = updatedBlocklistFilters;
      this.blocklistFilterInModal = null;

      this.metrics.trackJobEvent('Security - Blocked Person Record Deleted', {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'success',
      });

      const message_title = 'Deleted record';
      this.flashMessages.showAndHideFlash('success', message_title);
      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'success',
        message_title,
        message_code: 'blocked_person_delete_success',
        message_code_type: 'block_list',
      });
    } catch (e) {
      const error_message = parseErrorForDisplay(e);
      this.metrics.trackJobEvent('Security - Blocked Person Record Deleted', {
        block_list_filter_id: blocklistFilter.id,
        processing_result: 'failure',
        error_message,
      });

      const message_title = 'Delete failed';
      this.flashMessages.showAndHideFlash('error', message_title, 'Please try again.');
      this.metrics.trackJobEvent(`Viewed Flash Message`, {
        type: 'error',
        message_title,
        message_code: error_message,
        message_code_type: 'block_list',
      });
    }
  });

  @action
  createBlocklistFilter(): void {
    assert(
      'You must pass @filterParentRelationshipName to <Security::ConfigureBlocklist>',
      !isNone(this.args.filterParentRelationshipName),
    );
    assert('You must pass @filterParent function to <Security::ConfigureBlocklist>', !isNone(this.args.filterParent));
    const newBlocklistFilter = this.store.createRecord('blacklist-filter', {
      [this.args.filterParentRelationshipName]: this.args.filterParent,
    });
    this.blocklistFilterInModal = newBlocklistFilter;
    // A new record is editable unless the whole component is read-only; the per-row scope gate does
    // not apply to creation (it's gated separately by @hasCreatePermission).
    this.blocklistFilterModalReadOnly = Boolean(this.args.readOnly);
    this.metrics.startJob();
    this.metrics.trackJobEvent('Security - Nav to Add New Blocked Person Button Clicked', { button_text: 'Add new' });
  }

  @action
  editBlocklistFilter(blocklistFilter: BlacklistFilter): void {
    this.blocklistFilterInModal = blocklistFilter;
    // A row the current admin can't edit opens as a view-only modal. This gates the modal form, the
    // toggle, and the body-cell click paths — without it the "read-only" label was cosmetic.
    this.blocklistFilterModalReadOnly = this.isRowReadOnly(blocklistFilter);
    this.metrics.startJob();
    this.metrics.trackJobEvent('Security - Nav to Edit Blocked Person Button Clicked', { button_text: 'Edit' });
  }

  @action
  closeBlocklistFilterModal(blocklistFilter: BlacklistFilter): void {
    blocklistFilter?.rollbackAttributes();
    this.blocklistFilterInModal = null;
  }

  // Open the React scope modal for a row. EDIT-only — always from a persisted row; creation goes
  // through <BlocklistFilterModal>. Hence both scope gates use the `.update` permissions.
  openLocationScopeModal = (blocklistFilter: BlacklistFilter): void => {
    this.blocklistFilterToScope = blocklistFilter;
    this.metrics.trackEvent('Security - Blocklist Edit Locations Clicked', { button_text: 'Locations' });
  };

  // Mount SelectActiveLocationsModal into the portal element (did-insert).
  mountLocationScopeModal = async (): Promise<void> => {
    // Loop (not tail-recursion) so rapid supersession — N Locations cells clicked during one async
    // mount — settles on the LATEST pending filter without an unbounded mount chain. isDestroying
    // guards after each await prevent touching state on a torn-down component; any load/render error
    // resets the open flag so the table is never left with an invisible-but-"open" modal.
    while (this.blocklistFilterToScope) {
      const filter = this.blocklistFilterToScope;

      try {
        const { SelectActiveLocationsModal } = await import('reactRichComponents/visitors/SelectActiveLocationsModal');
        if (this.isDestroying || this.isDestroyed) return;
        if (typeof SelectActiveLocationsModal !== 'function') {
          this.flashMessages.showAndHideFlash('error', 'Could not load the locations editor.');
          this.blocklistFilterToScope = null;
          return;
        }

        const locationIds = (await filter.locations).slice().map((loc) => loc.id);
        if (this.isDestroying || this.isDestroyed) return;

        // The picker grays out non-linkable locations client-side via allowedLocationIds (null = no
        // restriction for a global admin); canEdit and initialScope/initialLocationIds are threaded so
        // the modal doesn't re-derive them. The BE policy (403) still backstops any out-of-scope save.
        const initialScope = filter.scope === 'all_locations' ? 'all_locations' : 'specific_locations';
        const root = await this.reactRenderer.render(SelectActiveLocationsModal, 'react__blocklist-location-scope', {
          isOpen: true,
          blocklistFilterId: filter.id,
          initialScope,
          initialLocationIds: locationIds,
          canEdit: filter.canEdit,
          canSelectAllLocations: this.canEditAllLocations,
          allowedLocationIds: this.allowedLocationIds,
          onSave: () => void this.onLocationScopeSaved(),
          onClose: () => this.closeLocationScopeModal(),
        });

        if (this.isDestroying || this.isDestroyed) {
          this.reactRenderer.unmount(root);
          return;
        }
        // Superseded by another row while rendering — unmount the orphan and loop to mount the latest
        // pending filter (or exit if it was just closed, i.e. blocklistFilterToScope is now null).
        if (this.blocklistFilterToScope !== filter) {
          this.reactRenderer.unmount(root);
          continue;
        }
        // Defensive: unmount any prior root before reassigning (the {{#if}} guard keeps this to one
        // at a time today, but this stays leak-safe if that invariant ever changes).
        if (this.scopeModalRoot) {
          this.reactRenderer.unmount(this.scopeModalRoot);
        }
        this.scopeModalRoot = root;
        return;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[blocklist] failed to open the locations scope editor', e);
        if (!this.isDestroying && !this.isDestroyed) {
          this.flashMessages.showAndHideFlash('error', 'Failed to open the locations editor.');
          this.blocklistFilterToScope = null;
        }
        return;
      }
    }
  };

  closeLocationScopeModal = (): void => {
    this.blocklistFilterToScope = null;
  };

  // will-destroy on the portal element: tear down the React root when the modal closes/unmounts.
  unmountLocationScopeModal = (): void => {
    if (this.scopeModalRoot) {
      this.reactRenderer.unmount(this.scopeModalRoot);
      this.scopeModalRoot = undefined;
    }
  };

  // The React modal saved successfully (its onSave); refresh through the loader task so the reload
  // carries the standard loading/error UI. Flash success only AFTER the reload and only when it
  // succeeded — otherwise the load task's own error flash would collide with a premature success.
  onLocationScopeSaved = async (): Promise<void> => {
    this.blocklistFilterToScope = null;
    await this.initBlocklistFiltersTask.perform();
    if (!this.blocklistLoadFailed) {
      this.flashMessages.showAndHideFlash('success', 'Locations updated.');
    }
  };

  @action
  showToggleConfirmation(blocklistFilter: BlacklistFilter): void {
    this.blocklistFilterToToggle = blocklistFilter;
  }

  @action
  closeToggleConfirmation(): void {
    this.blocklistFilterToToggle = null;
  }

  confirmToggleTask = task(async () => {
    const blocklistFilter = this.blocklistFilterToToggle;
    if (!blocklistFilter) return;

    const isBlocking = blocklistFilter.disabled; // disabled=true means currently unblocked, so we're blocking
    const personName = blocklistFilter.fullName || '';
    const truncatedName = personName.length > 40 ? `${personName.slice(0, 40)}...` : personName;

    // Close modal immediately to prevent flash when state changes
    this.blocklistFilterToToggle = null;

    try {
      blocklistFilter.disabled = !blocklistFilter.disabled;
      await blocklistFilter.save();

      if (isBlocking) {
        this.flashMessages.showAndHideFlash('success', 'Success', `${truncatedName} has been blocked.`);
      } else {
        this.flashMessages.showAndHideFlash('success', 'Success', `${truncatedName} has been unblocked.`);
      }
    } catch (_e) {
      blocklistFilter.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', 'Update failed', 'Please try again.');
    }
  });

  @action
  updateBlocklistContacts(users: User[]): void {
    this.hasChangedBlocklistContacts = true;
    this.args.onContactsChange?.(true);
    this.blocklistContacts = users;
  }

  @action
  showDeleteConfirmation(blocklistFilter: BlacklistFilter): void {
    this.blocklistFilterToDelete = blocklistFilter;
  }

  @action
  closeDeleteConfirmation(): void {
    this.blocklistFilterToDelete = null;
  }

  confirmDeleteTask = task(async () => {
    const filter = this.blocklistFilterToDelete;
    if (filter) {
      try {
        await this.deleteBlocklistFilterTask.perform(filter);
      } finally {
        this.blocklistFilterToDelete = null;
      }
    }
  });

  <template>
    <fieldset
      class="pb-8 min-w-0 space-y-8"
      {{didInsert this.onInsert}}
      {{willDestroy this.onDestroy}}
      data-test-blocklist-settings
    >
      <div class="mr-16">
        <h4>Alerts</h4>
        <div class="flow-root row">
          <div class="float-left box-border w-10/12">
            <p class="mb-8">
              {{if
                @readOnly
                "These admins are alerted when a visitor matches the block list, and can approve or deny flagged visitors."
                "Choose which admins to alert when a visitor is a block list match. These admins can also approve or deny flagged visitors."
              }}
            </p>
            <div class="flex">
              <div class="grow max-w-full" data-test-blocklist-contact-input>
                <PowerSelectMultiple
                  @ariaLabel="Choose admins to receive block list alerts"
                  @search={{this.searchUsersTask.perform}}
                  @searchEnabled={{true}}
                  @searchField="fullName"
                  @placeholder="Search administrators"
                  @allowClear={{true}}
                  @selected={{this.blocklistContacts}}
                  @onChange={{this.updateBlocklistContacts}}
                  @disabled={{@readOnly}}
                  as |blocklistContact|
                >
                  {{blocklistContact.fullName}}
                </PowerSelectMultiple>
              </div>

              <button
                data-test-single-save
                disabled={{this.saveBlocklistContactsTask.isRunning}}
                class="btn btn--small btn--brand ml-4 self-start
                  {{unless this.hasChangedBlocklistContacts 'invisible'}}"
                {{on "click" this.saveBlocklistContactsTask.perform}}
              >
                Save contacts
              </button>
            </div>

            <div class="text-sm text-carbon-50 mt-4">
              {{#if (has-block "admin-permission-text")}}
                {{yield to="admin-permission-text"}}
              {{/if}}
              {{#unless @readOnly}}
                Administrators can set notification preferences for block list alerts in their
                <LinkTo @route="profile.notifications">notification settings</LinkTo>.
              {{/unless}}
            </div>
          </div>
        </div>
      </div>

      <div class="mr-8 pt-16 border-t">
        <div class="flex items-center">
          <div class="flex-auto">
            <h4>People you block</h4>
            <p class="mb-0">
              These people will require approval to visit any of your locations where block list is enabled.
            </p>
          </div>

          <ButtonGroup class="shrink-0">
            {{#if this.initBlocklistFiltersTask.isIdle}}
              {{#unless @readOnly}}
                <button
                  data-test-blocklist-filter-export-button
                  type="button"
                  class="btn btn--small btn--outline"
                  disabled={{this.exportBlocklistFiltersAsCsv.isRunning}}
                  {{on "click" this.exportBlocklistFiltersAsCsv.perform}}
                >
                  Export
                </button>
              {{/unless}}
              {{#if (or @hasCreatePermission @hasUploadPermission)}}
                <div>
                  <DropdownMenu @left="-34" @top="10" @width="180">
                    <:trigger>
                      <button
                        data-test-blocklist-filter-add-record-menu-button
                        class="btn btn--small btn--brandOutline split-drop-button"
                        role="button"
                      >
                        Add records
                        <img
                          src="/assets/images/chevron-down.svg"
                          alt="show actions"
                          class="ml-2"
                          style="margin-bottom: 2px;"
                        />
                      </button>
                    </:trigger>
                    <:content as |itemClasses|>
                      {{#if @hasCreatePermission}}
                        <button
                          data-test-blocklist-filter-add-single-record-button
                          class="ui-button {{itemClasses}}"
                          role="button"
                          {{on "click" this.createBlocklistFilter}}
                        >
                          Add a single record
                        </button>
                      {{/if}}
                      {{#if @hasUploadPermission}}
                        <LinkTo
                          data-test-blocklist-filter-import-csv-button
                          class="ui-button {{itemClasses}}"
                          role="button"
                          @route={{@csvImportRoute}}
                        >
                          Import from CSV
                        </LinkTo>
                      {{/if}}
                    </:content>
                  </DropdownMenu>
                </div>
              {{/if}}
            {{/if}}
          </ButtonGroup>
        </div>

        {{#if (has-block "message")}}
          {{yield to="message"}}
        {{/if}}

        <LiquidUnless @predicate={{this.initBlocklistFiltersTask.isRunning}} @use="fade">
          <div class="flex items-center block-search-row space-x-4 h-18 w-full">
            <div class="flex-auto">
              {{this.filteredBlocklistFilters.length}}
              blocked records
              {{#if this.searchText}} matching "{{this.searchText}}"{{/if}}
            </div>
            {{#if this.showLocationScoping}}
              {{! This/All-locations view filter, right-aligned next to search (the count above grows
                  via flex-auto to push it right). }}
              <div class="flex-none" data-test-blocklist-location-view>
                <PowerSelect
                  @options={{this.locationViewOptions}}
                  @selected={{this.selectedLocationViewOption}}
                  @onChange={{this.onLocationViewSelect}}
                  @searchEnabled={{false}}
                  @triggerClass="blocklist-location-view-trigger !mb-0 w-48"
                  @ariaLabel="Filter blocklist by location"
                  as |option|
                >
                  {{option.label}}
                </PowerSelect>
              </div>
            {{/if}}
            <BlocklistFiltersSearchInput
              @name="search-blocklist-filters"
              @placeholder="Search blocklist records"
              @value={{this.searchText}}
              @onChange={{this.mutateSearchText}}
              @showClear={{if this.searchText true false}}
              @enabled={{this.aboveMinimumBlocklistRecordThreshold}}
              style="width:350px;"
            />
          </div>
        </LiquidUnless>
        {{#if this.hasBlocklistEnhancements}}
          {{! NEW TABLE - Feature: feat-blocklist-expiration-and-pause }}
          {{! SINGLE horizontal scroll axis: ONE outer element owns overflow-x; the header is
              position:sticky;top:0 INSIDE it so it scrolls horizontally WITH the body in lockstep (no
              JS scroll-sync, no column desync) and sticks vertically. The VirtualEach body owns ONLY
              the vertical axis (its root is overflow-y-auto, overflow-x:visible). min-w-max lets both
              the header and the rows grow to the shared grid width so the outer container scrolls. }}
          <div class="csv-table mt-2 [scrollbar-gutter:stable]">
            {{! Table header — shares the grid track template with every body row (newTableGridStyle)
                so the two can't drift. sticky top-0 keeps it pinned while the body scrolls vertically;
                because it lives in the SAME overflow-x container as the body it scrolls horizontally
                with it. }}
            <div
              class="grid px-6 border-b border-[#e9e9ea] text-sm leading-normal bg-carbon-5 text-carbon-50 sticky top-0 z-10"
              style={{this.newTableGridStyle}}
            >
              <div class="font-semibold flex items-center py-3 border-r border-carbon-10 pr-4">Visitor name</div>
              <div class="font-semibold flex items-center py-3 border-r border-carbon-10 pr-4">Added on</div>
              <div class="font-semibold flex items-center py-3 border-r border-carbon-10 pr-4">Added by</div>
              <div class="font-semibold flex items-center py-3 border-r border-carbon-10 pr-4">Reason</div>
              <div class="font-semibold flex items-center py-3 border-r border-carbon-10 pr-4 whitespace-nowrap">Expires
                after</div>
              {{#if this.showLocationScoping}}
                <div class="font-semibold flex items-center py-3 border-r border-carbon-10 pr-4">Locations</div>
              {{/if}}
              <div class="font-semibold flex items-center justify-center py-3">
                Blocked
                <span class="sr-only">, {{if @readOnly "view" "edit and delete"}} actions</span>
              </div>
            </div>
            <div class="csv-table-body text-sm">

              {{! min-w-max grows the VirtualEach root (and thus the rows) to the shared grid width so
                  it matches the sticky header inside the same overflow-x container. The VirtualEach
                  root is overflow-y-auto (vertical only) — it must NOT add a second overflow-x. }}
              <VirtualEach
                @items={{this.filteredBlocklistFilters}}
                @height={{580}}
                @itemHeight={{58}}
                @positionIndex={{0}}
                class="[scrollbar-gutter:stable]"
              >
                <:default as |blocklistFilter index|>
                  <div
                    class={{this.getRowClass blocklistFilter.disabled}}
                    style={{this.newTableGridStyle}}
                    data-test-blocklist-filter-id={{blocklistFilter.id}}
                    data-test-blocklist-filter-row={{index}}
                  >
                    {{! Visitor name (photo + name). Row cells are NOT clickable — the edit modal opens
                        only via the pencil control in the Blocked cell (Figma behavior change). }}
                    <div
                      class={{this.getColumnClass "flex items-center gap-2 min-w-0" blocklistFilter.disabled}}
                      data-test-blocklist-filter-row-fullname={{index}}
                    >
                      {{#if (and (eq blocklistFilter.id this.tempId) this.tempPhoto)}}
                        <div
                          data-test-blocklist-filter-row-photo-temp
                          class="rounded-full border blur-sm bg-cover bg-center flex-shrink-0"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" this.tempPhoto ");")
                          }}
                        >
                          <span class="sr-only">Photo upload in progress</span>
                        </div>
                      {{else if blocklistFilter.photo.thumb}}
                        {{! Photo thumbnail as div (not link) - nested interactive elements are invalid for a11y.
                            Full-size photo is viewable in the edit modal. }}
                        <div
                          data-test-blocklist-filter-row-photo
                          class="rounded-full border bg-cover bg-center flex-shrink-0"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" blocklistFilter.photo.thumb ");")
                          }}
                        >
                          <span class="sr-only">Photo thumbnail</span>
                        </div>
                      {{else}}
                        <div
                          data-test-blocklist-filter-row-photo-default
                          class="rounded-full bg-no-repeat bg-center bg-cover flex-shrink-0"
                          style="width:40px; height:40px; background-image:url(/assets/images/profile-avatar.svg)"
                        >
                          <span class="sr-only">No photo available</span>
                        </div>
                      {{/if}}
                      <span class="font-medium text-carbon-70">
                        <TruncateWithTooltip @value={{or blocklistFilter.fullName ""}} @length={{30}} @side="bottom" />
                      </span>
                    </div>

                    {{! Added on (date + time) - NEW column, no CSS conflict }}
                    <div class={{this.getColumnClass "min-w-0" blocklistFilter.disabled}}>
                      <div class="flex flex-col">
                        {{! template-lint-disable attribute-indentation }}
                        <span class="text-carbon-70">{{dateFormat
                            blocklistFilter.createdAt
                            "MMM d, yyyy"
                            timezone=this.timezone
                          }}</span>
                        <span class="text-carbon-50">{{dateFormat
                            blocklistFilter.createdAt
                            "h:mm aaa zzz"
                            timezone=this.timezone
                          }}</span>
                      </div>
                    </div>

                    {{! Added by (name + role) - NEW column, no CSS conflict }}
                    <div class={{this.getColumnClass "min-w-0" blocklistFilter.disabled}}>
                      <div class="flex flex-col justify-center">
                        {{#if blocklistFilter.createdByContext}}
                          <span class="text-carbon-70">
                            <TruncateWithTooltip
                              @value={{or blocklistFilter.createdByContext.name ""}}
                              @length={{20}}
                              @side="bottom"
                            />
                          </span>
                          <span class="text-carbon-50">
                            <TruncateWithTooltip
                              @value={{or blocklistFilter.createdByContext.role_name ""}}
                              @length={{20}}
                              @side="bottom"
                            />
                          </span>
                        {{else}}
                          <span class="text-carbon-70">
                            <TruncateWithTooltip
                              @value={{or blocklistFilter.createdBy ""}}
                              @length={{20}}
                              @side="bottom"
                            />
                          </span>
                        {{/if}}
                      </div>
                    </div>

                    {{! Reason - flexible column that grows to fill space }}
                    <div
                      class={{this.getColumnClass "min-w-0" blocklistFilter.disabled}}
                      data-test-blocklist-filter-row-reason={{index}}
                    >
                      <TruncateWithTooltip
                        @value={{or blocklistFilter.reason ""}}
                        @length={{32}}
                        @side="bottom"
                        class="text-carbon-70"
                      />
                    </div>

                    {{! Expires after - NEW column, no CSS conflict }}
                    <div
                      class="{{this.getColumnClass
                          'whitespace-nowrap flex items-center min-w-0'
                          blocklistFilter.disabled
                        }}
                        text-carbon-70 blocklist-expires-at"
                    >
                      {{if
                        blocklistFilter.expiresAt
                        (dateFormat blocklistFilter.expiresAt "MMM d, yyyy" timezone=this.timezone)
                        "Never"
                      }}
                    </div>

                    {{! Locations - NEW column, gated by feat-blocklist-location-scoping.
                        Label is a focusable button → opens the React scope modal. Blank for zone filters. }}
                    {{#if this.showLocationScoping}}
                      <div class={{this.getColumnClass "flex items-center min-w-0" blocklistFilter.disabled}}>
                        {{#if (this.locationScopeLabel blocklistFilter)}}
                          {{#if (this.isRowReadOnly blocklistFilter)}}
                            {{! Read-only: location admins can't edit multi/all-locations scope. }}
                            <span
                              class="text-carbon-70"
                              title="Editing this block requires admin access to all of its locations."
                              data-test-blocklist-filter-row-locations={{index}}
                            >
                              {{this.locationScopeLabel blocklistFilter}}
                            </span>
                          {{else}}
                            {{! !important overrides .btn--link's red with the gem-50 design-system link color. }}
                            <button
                              type="button"
                              class="btn btn--link p-0 no-underline hover:no-underline !text-gem-50 hover:!text-gem-50 text-left"
                              data-test-blocklist-filter-row-locations={{index}}
                              {{on "click" (fn this.openLocationScopeModal blocklistFilter)}}
                            >
                              {{this.locationScopeLabel blocklistFilter}}
                            </button>
                          {{/if}}
                        {{/if}}
                      </div>
                    {{/if}}

                    {{! Blocked cell — [toggle] [pencil/edit] [trash/delete] together (Figma). All three
                        gate on the BE's per-row edit permission (isRowReadOnly): editable → dark +
                        interactive; read-only → faded + disabled. The pencil is the ONLY entry to the
                        edit/view modal (row cells are no longer clickable). For a read-only row the
                        pencil stays clickable as a VIEW affordance (opens the view-only modal); the
                        toggle and delete are disabled. }}
                    <div
                      class="flex items-center justify-center gap-4"
                      data-test-blocklist-filter-row-enabled={{index}}
                    >
                      <SlideToggle
                        class="toggle--tiny"
                        @id={{concat "toggle-" blocklistFilter.id}}
                        @isChecked={{blocklistFilter.isBlocked}}
                        @onClick={{fn this.showToggleConfirmation blocklistFilter}}
                        @isDisabled={{this.isRowReadOnly blocklistFilter}}
                      />
                      <button
                        data-test-blocklist-filter-row-edit={{index}}
                        type="button"
                        aria-label={{if (this.isRowReadOnly blocklistFilter) "View" "Edit"}}
                        class="w-6 h-6 hover:opacity-70 flex items-center justify-center
                          {{if (this.isRowReadOnly blocklistFilter) 'opacity-40'}}"
                        title={{if (this.isRowReadOnly blocklistFilter) "View" "Edit"}}
                        {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      >
                        {{svgJar "edit-16" width="16" height="16" class="block w-4 h-4"}}
                      </button>
                      <button
                        data-test-blocklist-filter-row-delete={{index}}
                        type="button"
                        aria-label="Delete"
                        class="w-6 h-6 hover:opacity-70 flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:opacity-40"
                        title="Delete"
                        disabled={{this.isRowReadOnly blocklistFilter}}
                        {{on "click" (fn this.showDeleteConfirmation blocklistFilter)}}
                      >
                        {{svgJar "trash-16" width="16" height="16" class="block w-4 h-4"}}
                      </button>
                    </div>
                  </div>
                </:default>
                <:inverse>
                  <div class="csv-table-empty p-4 text-carbon-50" data-test-blocklist-filter-row-empty-list>
                    {{#if this.initBlocklistFiltersTask.isRunning}}
                      <LoadingSpinnerPulse />
                    {{else if this.blocklistFilters.length}}
                      Your search for "{{this.searchText}}" does not match any records.
                    {{else if this.blocklistLoadFailed}}
                      There was a problem loading the block list.
                    {{else}}
                      No one blocked yet. Blocked people will appear here.
                    {{/if}}
                  </div>
                </:inverse>
              </VirtualEach>
            </div>
          </div>
        {{else}}
          {{! ORIGINAL TABLE - when feature flag is off }}
          <div class="csv-table mt-2">
            <div class="csv-table-header-row flex text-xs text-carbon-50 font-bold">
              <div class="blocklist-photo table-cell py-2 ml-4 mr-3">
                Photo
              </div>
              <div class="blocklist-name table-cell pr-4 pl-3 py-2">
                Full name
              </div>
              <div class="blocklist-aliases table-cell pr-4 pl-3 py-2">
                Aliases
              </div>
              <div class="blocklist-emails table-cell pr-4 pl-3 py-2">
                Email addresses
              </div>
              <div class="blocklist-phones table-cell pr-4 pl-3 py-2">
                Phone Numbers
              </div>
              <div class="blocklist-description table-cell pr-4 pl-3 py-2">
                Description
              </div>
              <div class="blocklist-keywords table-cell pr-4 pl-3 py-2">
                Keywords
              </div>
              <div class="blocklist-reason table-cell pr-4 pl-3 py-2">
                Reason for blocking
              </div>
              <div class="blocklist-action table-cell pr-4 pl-3 py-2">
                <span class="sr-only">{{if @readOnly "View" "Edit"}}</span>
              </div>
            </div>
            <div class="csv-table-body flex text-sm">

              <VirtualEach
                @items={{this.filteredBlocklistFilters}}
                @height={{580}}
                @itemHeight={{58}}
                @positionIndex={{0}}
              >
                <:default as |blocklistFilter index|>
                  <div
                    class="csv-table-row flex border-b"
                    data-test-blocklist-filter-id={{blocklistFilter.id}}
                    data-test-blocklist-filter-row={{index}}
                  >
                    <div class="blocklist-photo table-cell pb-2 pt-1 align-middle ml-4">
                      {{#if (and (eq blocklistFilter.id this.tempId) this.tempPhoto)}}
                        <div
                          data-test-blocklist-filter-row-photo-temp
                          class="block my-1 border blur-2 bg-cover bg-center"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" this.tempPhoto ");")
                          }}
                        >
                          <span class="sr-only">Photo upload in progress </span>
                        </div>
                      {{else if blocklistFilter.photo.thumb}}
                        <a
                          data-test-blocklist-filter-row-photo
                          href={{blocklistFilter.photo.original}}
                          target="_blank"
                          rel="noopener"
                          class="block my-1 border bg-cover bg-center"
                          style={{htmlSafe
                            (concat "width:40px; height:40px; background-image:url(" blocklistFilter.photo.thumb ");")
                          }}
                        >
                          <span class="sr-only">View full size photo</span>
                        </a>
                      {{else}}
                        <div
                          data-test-blocklist-filter-row-photo-default
                          class="my-1 border bg-carbon-5 bg-no-repeat bg-center"
                          style="width:40px; height:40px; background-image:url(/assets/images/profile.svg)"
                        >
                          <span class="sr-only">No photo available</span>
                        </div>
                      {{/if}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-name px-4 py-2 align-middle font-normal"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-fullname={{index}}
                    >
                      {{truncate blocklistFilter.fullName 25 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-aliases px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-aliases={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.aliases) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-emails px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-emails={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.emails) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-phones px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-phones={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.phoneNumbers) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-description px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-description={{index}}
                    >
                      {{truncate blocklistFilter.description 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-keywords px-4 py-2 align-middle"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-keywords={{index}}
                    >
                      {{truncate (join ", " blocklistFilter.otherKeywords) 30 true}}
                    </div>
                    <div
                      role="button"
                      class="table-cell blocklist-reason px-4 py-2 align-middle break-words break-all"
                      {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                      data-test-blocklist-filter-row-reason={{index}}
                    >
                      <p>{{truncate blocklistFilter.reason 80 true}}</p>
                    </div>
                    <div class="blocklist-action table-cell px-4 py-2 align-middle text-center">
                      <div class="flex items-center h-full">
                        <button
                          data-test-blocklist-filter-row-edit={{index}}
                          type="button"
                          class="btn btn--link"
                          {{on "click" (fn this.editBlocklistFilter blocklistFilter)}}
                        >
                          {{#if @readOnly}}
                            View
                          {{else}}
                            <img src="/assets/images/pencil.svg" alt="edit" class="p-2 box-content" />
                          {{/if}}
                        </button>
                      </div>
                    </div>
                  </div>
                </:default>
                <:inverse>
                  <div class="csv-table-empty p-4 text-carbon-50" data-test-blocklist-filter-row-empty-list>
                    {{#if this.initBlocklistFiltersTask.isRunning}}
                      <LoadingSpinnerPulse />
                    {{else if this.blocklistFilters.length}}
                      Your search for "{{this.searchText}}" does not match any records.
                    {{else if this.blocklistLoadFailed}}
                      There was a problem loading the block list.
                    {{else}}
                      No one blocked yet. Blocked people will appear here.
                    {{/if}}
                  </div>
                </:inverse>
              </VirtualEach>
            </div>
          </div>
        {{/if}}
      </div>
    </fieldset>

    {{#if this.blocklistFilterInModal}}
      <BlocklistFilterModal
        @blocklistFilter={{this.blocklistFilterInModal}}
        @close={{fn this.closeBlocklistFilterModal this.blocklistFilterInModal}}
        @delete={{fn this.deleteBlocklistFilterTask.perform this.blocklistFilterInModal}}
        @isSaving={{this.saveBlocklistFilterTask.isRunning}}
        @maxPhotoSizeKb={{10000}}
        @save={{fn this.saveBlocklistFilterTask.perform this.blocklistFilterInModal}}
        @readOnly={{this.blocklistFilterModalReadOnly}}
      />
    {{/if}}

    {{#if this.blocklistFilterToScope}}
      {{! Portal for the React SelectActiveLocationsModal, mounted via reactRenderer. }}
      <div
        id="react__blocklist-location-scope"
        {{didInsert this.mountLocationScopeModal}}
        {{willDestroy this.unmountLocationScopeModal}}
      ></div>
    {{/if}}

    {{#if this.hasBlocklistEnhancements}}
      {{! NEW MODALS - Feature: feat-blocklist-expiration-and-pause }}
      {{#if this.blocklistFilterToDelete}}
        <ConfirmationModal
          @title="Delete blocked person"
          @ctaText="Delete"
          @handleTaskAction={{this.confirmDeleteTask}}
          @onClose={{this.closeDeleteConfirmation}}
          @closeButtonClass="btn--outline hover:bg-carbon-5"
          @showTopRightCloseButton={{true}}
          @containerClass="!max-w-[550px]"
          data-test-delete-blocklist-filter-modal
        >
          <p class="mt-4 text-carbon-60">
            Are you sure you want to delete
            <strong class="text-carbon-70">{{this.blocklistFilterToDelete.fullName}}</strong>
            from the block list? This action cannot be undone.
          </p>
        </ConfirmationModal>
      {{/if}}

      {{#if this.blocklistFilterToToggle}}
        <ConfirmationModal
          @title={{if this.isBlockingAction "Block visitor" "Unblock visitor"}}
          @ctaText={{if this.isBlockingAction "Block" "Unblock"}}
          @handleTaskAction={{this.confirmToggleTask}}
          @onClose={{this.closeToggleConfirmation}}
          @closeButtonClass="btn--outline hover:bg-carbon-5"
          @showTopRightCloseButton={{true}}
          @containerClass="!max-w-[550px]"
          data-test-toggle-blocklist-filter-modal
        >
          <p class="mt-4 text-carbon-60">
            {{#if this.isBlockingAction}}
              Are you sure you want to block
              <strong class="text-carbon-70">{{this.blocklistFilterToToggle.fullName}}</strong>? They will be denied
              entry at this location.
            {{else}}
              Are you sure you want to unblock
              <strong class="text-carbon-70">{{this.blocklistFilterToToggle.fullName}}</strong>? They will be allowed
              access at this location.
            {{/if}}
          </p>
        </ConfirmationModal>
      {{/if}}
    {{/if}}
  </template>
}
